import { EyeOff, Eye, Globe01, Map01, Move } from '@untitled-ui/icons-react/build/cjs';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { logError } from 'src/services/LoggingService';

import globe01 from '../../assets/img/icons/filled-icons/globe-01.svg';
import { PATH } from '../../constants/path';
import { listTracks } from '../../services/RadarService';
import { extractCityOrStateName } from '../../utils/CommonFunctions';
import WalToast from '../../utils/WalToast';

import useTrackHeaderTheme from './hooks/useTrackHeaderTheme';
import { createTrackDetailedPopupContent, createMarkerSvg, hideTooltipsAtCoordinates, showTooltipsAtCoordinates, calculateTooltipPosition } from './track.marker';
import { readTrackMapPreferences, readTrackViewMode, TrackViewMode, writeTrackMapPreferences, writeTrackViewMode } from './track.preferences';
import {
    trackParams as trackParamsSelector,
    trackList as trackListSelector,
    setTrackList,
    setTrackTotalRecords,
    setTrackLoading,
    setOverviewLoading,
    setTrackCount,
    initialTrackCount,
    setTrackParams,
    setTrackPortalVisibility,
    type ITrackOrder,
    type ITrackParams,
} from './track.slice';
import { getTrackOrderCoordinates, getTrackOrderHealth, getTrackRolloverState, isTrackRolloverDue, TRACK_HEALTH_COLORS, TRACK_HEALTH_HOVER_COLORS, TRACK_HEALTH_WEIGHTS } from './track.utils';
import TrackHeaderClock from './TrackHeaderClock';
import TrackLeftSidebar from './TrackLeftSidebar';
import { consumeCameFromDetail, getActiveTrackView, setActiveTrackView, useTrackMap } from './TrackMapContext';
import TrackMapThemePicker from './TrackMapThemePicker';
import TrackTableView from './TrackTableView';
import TrackTimezoneToggle from './TrackTimezoneToggle';

// eslint-disable-next-line max-lines-per-function
const Track = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const trackParams = useAppSelector(trackParamsSelector);
    const trackList = useAppSelector(trackListSelector);
    const appearance = useAppSelector(appearanceSelector);
    const currentUser = useAppSelector(currentUserSelector);
    const userId: number | string | undefined = currentUser?.id;
    const timezone = appearance?.timezone ?? 'UTC';

    // Shared persistent Mapbox instance from TrackMapLayout.
    const { mapRef, isMapReady, attachMapTo, returnToStash, styleEpoch } = useTrackMap();

    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const sortToMapReload = useRef<boolean>(true);
    // Always points to the latest createCustomMarker closure — avoids stale captures in async map callbacks
    const createCustomMarkerRef = useRef<any>(null);
    // Tracks the currently visible sidebar-hover popup so it can be removed on mouse leave
    const sidebarPopupRef = useRef<mapboxgl.Popup | null>(null);
    // Tracks the last hovered order's coords so we can restore its small tooltip on hover end
    const hoveredOrderCoordsRef = useRef<[number, number] | null>(null);
    // Tracks previous trackParams to detect sub-status-only changes (skip overview skeleton in that case)
    const prevTrackParamsRef = useRef<ITrackParams | null>(null);
    // Tracks the running marker-update cycle so stale setTimeout retries are ignored
    const markerUpdateIdRef = useRef<number>(0);
    // One-shot guard for the initial camera animation — prevents a second run
    // (when isMapReady flips) from clobbering the back-from-detail animation
    // already queued in the first run.
    const hasInitialCameraRunRef = useRef<boolean>(false);
    // Flips to false only on REAL unmount (tracked by the isMountedRef tracker
    // effect). Lets the main cleanup distinguish "deps changed → don't tear down
    // map state" from "actually unmounting → do tear down".
    const isMountedRef = useRef<boolean>(true);

    const [isVisible, setIsVisible] = useState(true);
    // Map vs Table is a deliberate choice, so a reload must land back on it instead of resetting
    // to the map. Read synchronously (localStorage, per user) so the chosen surface paints first
    // rather than flashing the map for a frame. Same treatment as mapProjection below.
    const [viewActiveTab, setViewActiveTab] = useState<TrackViewMode>(() => readTrackViewMode(userId));

    // Map projection (flat mercator ⇄ 3D globe). Deliberately LOCAL to the list page and
    // not on TrackMapContext: the detail page always forces 'mercator', so it has no
    // projection control to share (mirrors wal-frontend-v2 Radar). Persisted per user in
    // localStorage (track.preferences.ts) so a globe selection survives a reload.
    const [mapProjection, setMapProjection] = useState<'mercator' | 'globe'>(() => readTrackMapPreferences(userId).projection);
    // Theme-adaptive header classes — shared with the detail page via useTrackHeaderTheme,
    // so both headers restyle identically as the map's base style/projection change.
    const trackHeaderThemeClass = useTrackHeaderTheme(mapProjection);
    // Ref mirror so initializeMap can restore the choice without listing mapProjection
    // as a dependency (which would re-run the whole map setup on every toggle).
    const mapProjectionRef = useRef(mapProjection);
    useEffect(() => {
        mapProjectionRef.current = mapProjection;
    }, [mapProjection]);

    // Apply the saved projection once per user, in case `currentUser.id` was not yet
    // available when the useState initialiser ran. Same guard pattern as
    // TrackMapLayout's hydration effect.
    // `auth` is redux-persisted so the id is normally known on the first render; when it lands a
    // tick later the initializer above ran with no storage key, so re-read once it exists.
    // Restoring the view alone is enough — limit already defaults to the table view's 25, and
    // only an explicit switch to the map raises it — so no refetch is triggered here.
    const viewModeHydratedForUserRef = useRef<number | string | null>(null);
    useEffect(() => {
        if (!userId || viewModeHydratedForUserRef.current === userId) return;

        viewModeHydratedForUserRef.current = userId;
        setViewActiveTab(readTrackViewMode(userId));
    }, [userId]);

    const projectionHydratedForUserRef = useRef<number | string | null>(null);
    useEffect(() => {
        if (!userId || projectionHydratedForUserRef.current === userId) return;

        projectionHydratedForUserRef.current = userId;

        const { projection } = readTrackMapPreferences(userId);

        setMapProjection((prev) => {
            if (prev === projection) return prev;

            mapProjectionRef.current = projection;
            // Projection is map-instance state (not style state), so this needs no
            // source/layer re-add — same reasoning as toggleProjection.
            mapRef.current?.setProjection(projection);

            return projection;
        });
    }, [userId, mapRef]);

    // Keep a ref so camera helpers always read the latest sidebar state without
    // being listed as dependencies on every callback.
    const isVisibleRef = useRef(isVisible);
    useEffect(() => {
        isVisibleRef.current = isVisible;
    }, [isVisible]);

    // Skip the first fire (mount) so we only react to actual sidebar toggles.
    const sidebarToggleInitRef = useRef(false);

    // Geographic bounds that cover all of North America (US + Canada).
    // fitBounds auto-calculates the correct center and zoom for any screen
    // size, so the view is always consistent — no hardcoded coordinates needed.
    const NORTH_AMERICA_BOUNDS: [[number, number], [number, number]] = [
        [-130.8852, 24.9117],
        [-59.4326, 60.5695],
    ];
    // Width of the absolute-positioned order-list sidebar overlay.
    const SIDEBAR_WIDTH = 537;

    // Animate the map to always show the full North America overview.
    // Uses fitBounds so the result is identical on every screen size and
    // accounts for the sidebar overlay via dynamic left padding.
    const fitNorthAmerica = useCallback(
        (options?: { duration?: number; animate?: boolean; easing?: (t: number) => number }) => {
            if (!mapRef.current) return;

            mapRef.current.fitBounds(NORTH_AMERICA_BOUNDS, {
                padding: {
                    top: 40,
                    right: 40,
                    bottom: 40,
                    // Shift the visible area right of the sidebar when it is open
                    left: isVisibleRef.current ? SIDEBAR_WIDTH + 40 : 40,
                },
                duration: options?.duration ?? 600,
                ...(options?.animate === false ? { animate: false } : {}),
                ...(options?.easing && { easing: options.easing }),
                essential: true,
            });
        },
        [mapRef]
    );

    // Re-center when the sidebar opens or closes so the visible area always
    // frames the same geographic region (fitBounds recalculates left padding).
    // sidebarToggleInitRef skips the first fire on mount.
    useEffect(() => {
        if (!sidebarToggleInitRef.current) {
            sidebarToggleInitRef.current = true;

            return;
        }

        if (!mapRef.current?.isStyleLoaded()) return;
        fitNorthAmerica({ duration: 300 });
    }, [isVisible, fitNorthAmerica]);

    const handleViewChange = (opt: any) => {
        const next = opt?.value;
        setViewActiveTab(next);
        writeTrackViewMode(userId, next);

        if (next === 'table_view') {
            dispatch(setTrackParams({ page: 1, limit: 25 }));
        } else if (next === 'map_view') {
            dispatch(setTrackParams({ page: 1, limit: 100 }));
        }
    };

    useEffect(() => {
        if (!trackParams.startDate) return;

        // Only sub-status changed — skip overview skeleton (counts are unaffected by filterBySubStatus)
        const prev = prevTrackParamsRef.current;
        const isSubStatusOnlyChange = prev !== null && (Object.keys(trackParams) as (keyof ITrackParams)[]).every((k) => k === 'filterBySubStatus' || trackParams[k] === prev[k]);

        prevTrackParamsRef.current = trackParams;

        dispatch(setTrackLoading(true));

        if (!isSubStatusOnlyChange) {
            dispatch(setOverviewLoading(true));
        }

        listTracks({
            ...trackParams,
            originSearch: extractCityOrStateName(trackParams.originSearch),
            destinationSearch: extractCityOrStateName(trackParams.destinationSearch),
        })
            .then((response: any) => {
                dispatch(setTrackList(response?.data ?? []));
                // The list is server-paged, so the pager needs the API's total, not this page's row count.
                dispatch(setTrackTotalRecords(Number(response?.pagination?.totalRecords ?? response?.data?.length ?? 0)));
                dispatch(setTrackCount(response?.overview ?? initialTrackCount));
                dispatch(setTrackPortalVisibility(response?.portalVisibility ?? null));
            })
            .catch(() => {
                WalToast.error(t('Common.Something_went_wrong'));
            })
            .finally(() => {
                dispatch(setTrackLoading(false));
                dispatch(setOverviewLoading(false));
            });
    }, [trackParams]);

    const filterArr = useMemo(
        () => [
            { label: 'Map View', value: 'map_view' },
            { label: 'Table View', value: 'table_view' },
        ],
        []
    );

    // Memoized tooltip positioning with map reference
    const calculatePosition = useCallback((coordinates: [number, number]) => calculateTooltipPosition(coordinates, mapRef.current), []);

    // Show detailed popup on the map marker corresponding to the hovered list card
    const showOrderDetailPopup = useCallback(
        (order: ITrackOrder) => {
            if (!mapRef.current) return;
            const coords = getTrackOrderCoordinates(order);

            if (!coords) return;

            // Dismiss any previous sidebar popup before opening a new one
            if (sidebarPopupRef.current) {
                sidebarPopupRef.current.remove();
                sidebarPopupRef.current = null;
            }

            hideTooltipsAtCoordinates(coords);
            hoveredOrderCoordsRef.current = coords;

            const positioning = calculatePosition(coords);
            const popupContent = createTrackDetailedPopupContent(order, navigate, PATH, timezone);

            sidebarPopupRef.current = new mapboxgl.Popup({
                offset: positioning.popupOffset,
                closeButton: false,
                closeOnClick: false,
                maxWidth: '320px',
                anchor: positioning.popupAnchor,
            })
                .setLngLat(coords)
                .setDOMContent(popupContent)
                .addTo(mapRef.current);
        },
        [calculatePosition, navigate, timezone]
    );

    // Remove the sidebar popup and restore the small order-ID tooltip
    const hideOrderDetailPopup = useCallback(() => {
        if (sidebarPopupRef.current) {
            sidebarPopupRef.current.remove();
            sidebarPopupRef.current = null;
        }

        if (hoveredOrderCoordsRef.current) {
            showTooltipsAtCoordinates(hoveredOrderCoordsRef.current);
            hoveredOrderCoordsRef.current = null;
        }
    }, []);

    // Create a DOM element for a single order marker with tooltip and hover popup
    const createCustomMarker = useCallback(
        (orderData: any, coordinates?: [number, number], tz: string = timezone) => {
            // A Show-as-Multi-Leg order renders one card/marker PER LEG, so the master id
            // alone is no longer unique — suffix with the leg's DB id to keep DOM ids valid.
            const markerKey = `${orderData.masterOrderFullId || ''}-${orderData.legDbId ?? ''}`;

            const parentDiv = document.createElement('div');
            parentDiv.className = 'parent-marker';
            parentDiv.id = markerKey;

            const el = document.createElement('div');
            el.className = 'marker';
            el.id = markerKey;

            const customerImageDiv = document.createElement('div');
            customerImageDiv.className = `parcel-arrow hover-parcel-arrow marker-${markerKey}`;
            customerImageDiv.id = markerKey;
            // Color the arrow circle by health: red=delayed, orange=pickup rollover due,
            // blue=on track / pending. `tz` anchors "now" for the rollover calc.
            const health = getTrackOrderHealth(orderData, isTrackRolloverDue(orderData, getTrackRolloverState(orderData, tz)));
            customerImageDiv.style.backgroundColor = TRACK_HEALTH_COLORS[health];
            customerImageDiv.style.setProperty('--marker-hover-bg', TRACK_HEALTH_HOVER_COLORS[health]);
            customerImageDiv.innerHTML = createMarkerSvg();

            const tooltipLabel = document.createElement('div');
            tooltipLabel.className = 'order-tooltip';
            tooltipLabel.style.cssText = `
        position: absolute;
        top: -30px;
        left: 50%;
        transform: translateX(-50%);
        background-color: rgba(0, 0, 0, 0.8);
        padding: 4px 8px;
        font-weight: 500;
        white-space: nowrap;
        z-index: 10;
        pointer-events: none;
        border-radius: 6px;
        background-color: #0c111d;
        color: #fff;
        font-size: 14px;
        font-family: 'Inter', sans-serif;
      `;
            tooltipLabel.textContent = orderData.masterOrderFullId ? `#${orderData.masterOrderFullId}` : '';

            // Calculate smart popup anchor so it never renders off-screen
            let positioning: {
                popupAnchor: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
                popupOffset: number | [number, number];
            } = { popupAnchor: 'bottom', popupOffset: 15 };

            if (coordinates) {
                positioning = calculatePosition(coordinates);
            }

            parentDiv.appendChild(customerImageDiv);
            parentDiv.appendChild(tooltipLabel);

            (parentDiv as any)._coordinates = coordinates;
            (parentDiv as any)._positioning = positioning;

            parentDiv.addEventListener('mouseenter', (e) => {
                e.stopPropagation();
                const markerCoords = (parentDiv as any)._coordinates;

                if (markerCoords) {
                    hideTooltipsAtCoordinates(markerCoords);

                    const popupContent = createTrackDetailedPopupContent(orderData, navigate, PATH, tz);
                    const currentPositioning = (parentDiv as any)._positioning;

                    if (currentPositioning) {
                        const popup = new mapboxgl.Popup({
                            offset: currentPositioning.popupOffset,
                            closeButton: false,
                            closeOnClick: false,
                            maxWidth: '320px',
                            anchor: currentPositioning.popupAnchor,
                        })
                            .setLngLat(markerCoords)
                            .setDOMContent(popupContent)
                            .addTo(mapRef.current!);

                        (parentDiv as any)._popup = popup;
                    }
                }
            });

            parentDiv.addEventListener('mouseleave', (e) => {
                e.stopPropagation();
                const popup = (parentDiv as any)._popup;

                if (popup) {
                    popup.remove();
                    (parentDiv as any)._popup = null;
                }

                const markerCoords = (parentDiv as any)._coordinates;

                if (markerCoords) {
                    showTooltipsAtCoordinates(markerCoords);
                }
            });

            // Recalculate popup anchor on map move/zoom so it stays accurate
            const updatePopupPosition = () => {
                if (coordinates) {
                    const newPositioning = calculatePosition(coordinates);
                    (parentDiv as any)._positioning = newPositioning;
                }
            };

            (parentDiv as any)._updatePosition = updatePopupPosition;

            return parentDiv;
        },
        [calculatePosition, navigate, timezone]
    );

    // Keep ref in sync with latest closure
    useEffect(() => {
        createCustomMarkerRef.current = createCustomMarker;
    }, [createCustomMarker]);

    const removeAllMarkersFromDOM = () => {
        document.querySelectorAll('.mapboxgl-marker').forEach((m) => m.remove());
        document.querySelectorAll('.mapboxgl-popup').forEach((p) => p.remove());
    };

    const removeAllMarkers = () => {
        if (!sortToMapReload.current) return;

        if (mapRef.current && (mapRef.current as any)._clearAllMarkers) {
            (mapRef.current as any)._clearAllMarkers();
        }

        if (mapRef.current?.getSource('orders')) {
            (mapRef.current.getSource('orders') as mapboxgl.GeoJSONSource).setData({
                type: 'FeatureCollection',
                features: [],
            });
        }

        removeAllMarkersFromDOM();
    };

    // Plot markers for all orders in trackList. Guarded by getActiveTrackView()
    // so stale retries that fire after detail-page mount don't repopulate the
    // orders source on top of the detail's route line.
    const setOrderMarker = useCallback(async () => {
        if (!sortToMapReload.current) return;
        if (getActiveTrackView() !== 'list') return;

        // Bump version so any previous pending setTimeout becomes stale
        const updateId = ++markerUpdateIdRef.current;

        if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
            setTimeout(() => {
                if (markerUpdateIdRef.current === updateId && getActiveTrackView() === 'list') {
                    setOrderMarker();
                }
            }, 100);

            return;
        }

        const ordersSource = mapRef.current.getSource('orders');

        if (!ordersSource) {
            setTimeout(() => {
                if (markerUpdateIdRef.current === updateId && getActiveTrackView() === 'list') {
                    setOrderMarker();
                }
            }, 100);

            return;
        }

        if (markerUpdateIdRef.current !== updateId) return;
        if (getActiveTrackView() !== 'list') return;

        removeAllMarkers();

        const features: any[] = [];

        for (const order of trackList) {
            const coords = getTrackOrderCoordinates(order);

            if (!coords) {
                console.warn(`No coordinates for order ${order.masterOrderFullId}`);
                continue;
            }

            const orderHealth = getTrackOrderHealth(order, isTrackRolloverDue(order, getTrackRolloverState(order, timezone)));

            features.push({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: coords },
                properties: {
                    masterOrderFullId: order.masterOrderFullId,
                    health: orderHealth,
                    // Numeric severity (0 blue / 1 orange / 2 red) so the cluster layer
                    // can pick the worst-contained load via a `max` accumulation.
                    needsActionWeight: TRACK_HEALTH_WEIGHTS[orderHealth],
                    redCount: orderHealth === 'red' ? 1 : 0,
                    orangeCount: orderHealth === 'orange' ? 1 : 0,
                    orderData: JSON.stringify(order),
                },
            });
        }

        if (markerUpdateIdRef.current !== updateId) return;
        if (getActiveTrackView() !== 'list') return;

        (ordersSource as mapboxgl.GeoJSONSource).setData({
            type: 'FeatureCollection',
            features,
        });

        if (trackList.length === 0) {
            fitNorthAmerica({ duration: 500 });
        }
    }, [trackList, fitNorthAmerica, timezone]);

    // Adds the clustered `orders` source and its two layers. Kept SEPARATE from
    // registerMapListeners and made idempotent because map.setStyle() destroys every
    // style-owned source/layer — this is the part that must be re-runnable after a
    // theme change, while the listeners below must NOT be re-registered.
    const addOrdersSourceAndLayers = useCallback(() => {
        if (!mapRef.current || mapRef.current.getSource('orders')) return;

        // GeoJSON source with built-in clustering
        mapRef.current!.addSource('orders', {
            type: 'geojson',
            generateId: true,
            data: { type: 'FeatureCollection', features: [] },
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
            // Aggregate worst health across clustered loads so the circle
            // can be colored by the most severe order inside it.
            // needsActionWeight: 2=red, 1=orange, 0=blue
            clusterProperties: {
                maxHealthWeight: ['max', ['get', 'needsActionWeight']],
                redCount: ['+', ['get', 'redCount']],
                orangeCount: ['+', ['get', 'orangeCount']],
            },
        });

        // Cluster circle layer — only shown when count > 1
        mapRef.current!.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'orders',
            filter: ['all', ['has', 'point_count'], ['>', ['get', 'point_count'], 1]],
            paint: {
                // Color by worst-contained health: red=delayed,
                // orange=pickup rollover due, blue=on track / pending
                'circle-color': ['case', ['==', ['get', 'maxHealthWeight'], 2], TRACK_HEALTH_COLORS.red, ['==', ['get', 'maxHealthWeight'], 1], TRACK_HEALTH_COLORS.orange, TRACK_HEALTH_COLORS.blue],
                'circle-radius': 12,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
            },
        });

        // Cluster count label — shows total orders inside each cluster
        mapRef.current!.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'orders',
            filter: ['all', ['has', 'point_count'], ['>', ['get', 'point_count'], 1]],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 14,
            },
            paint: { 'text-color': '#ffffff' },
        });
    }, [mapRef]);

    // Registers every map event listener exactly ONCE per map instance, and owns the
    // marker registries the render listener closes over.
    //
    // Guarded by a flag on the MAP OBJECT, not a component ref and not getSource('orders'):
    //   - the map outlives page remounts, so a component ref would re-bind on every remount
    //   - getSource('orders') goes false after setStyle, so reusing it would double-bind
    //     every listener on each theme change (duplicate navigations, doubled updateMarkers)
    //
    // `markers`/`markersOnScreen`/`clearAllMarkers`/`updateMarkers` live HERE and not with
    // the source: if they were re-created per style switch, the once-bound 'render' listener
    // would still close over the old dicts, orphaning markers that could never be removed.
    //
    // Layer-scoped listeners ('clusters') are delegated — mapbox resolves the layer at event
    // time — so they survive setStyle and re-activate once the layer is re-added.
    const registerMapListeners = useCallback(() => {
        if (!mapRef.current) return;
        if ((mapRef.current as any)._trackListenersBound) return;
        (mapRef.current as any)._trackListenersBound = true;

        // Click a cluster → zoom in to expand it
        mapRef.current!.on('click', 'clusters', (e) => {
            const features = mapRef.current!.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const clusterId = features[0].properties!.cluster_id;
            (mapRef.current!.getSource('orders') as mapboxgl.GeoJSONSource).getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || zoom == null || typeof zoom !== 'number') return;
                mapRef.current!.easeTo({ center: (features[0].geometry as any).coordinates, zoom });
            });
        });

        mapRef.current!.on('mouseenter', 'clusters', () => {
            mapRef.current!.getCanvas().style.cursor = 'pointer';
        });
        mapRef.current!.on('mouseleave', 'clusters', () => {
            mapRef.current!.getCanvas().style.cursor = '';
        });

        // Per-frame marker management for individual points and single-item clusters
        let markers: { [key: string]: mapboxgl.Marker } = {};
        let markersOnScreen: { [key: string]: mapboxgl.Marker } = {};

        const clearAllMarkers = () => {
            document.querySelectorAll('.mapboxgl-popup').forEach((p) => p.remove());

            for (const id in markers) markers[id].remove();

            for (const id in markersOnScreen) markersOnScreen[id].remove();

            markers = {};
            markersOnScreen = {};
        };

        (mapRef.current as any)._clearAllMarkers = clearAllMarkers;

        const updateMarkers = () => {
            const newMarkers: { [key: string]: mapboxgl.Marker } = {};
            const features = mapRef.current!.querySourceFeatures('orders');

            for (const feature of features) {
                const coords = (feature.geometry as any).coordinates as [number, number];
                const props = feature.properties!;

                if (!props.cluster) {
                    // Single unclustered point
                    const id = `unclustered-${feature.id}`;
                    let marker = markers[id];

                    if (!marker) {
                        try {
                            const orderData = JSON.parse(props.orderData);
                            const el = createCustomMarkerRef.current?.(orderData, coords);
                            marker = markers[id] = new mapboxgl.Marker({ element: el }).setLngLat(coords);

                            el.addEventListener('click', () => {
                                if (orderData.masterOrderId) {
                                    navigate(`${PATH.TRACK}/${orderData.masterOrderId}`);
                                }
                            });
                        } catch (parseError) {
                            logError(parseError, {
                                errorMessage: 'Failed to parse order data for marker',
                                errorLocation: 'Track/index: renderMarkers',
                                storeInDatabase: true,
                                logToConsole: true,
                            });
                        }
                    }

                    newMarkers[id] = marker;
                    if (!markersOnScreen[id]) marker.addTo(mapRef.current!);
                } else if (props.cluster && props.point_count === 1) {
                    // Cluster containing exactly one point — render it as a normal marker
                    const id = `cluster-${props.cluster_id}`;
                    const existingMarker = markers[id];

                    if (!existingMarker) {
                        const clusterInfo = { clusterId: props.cluster_id, coordinates: coords, markerId: id };
                        const currentMarkers = markers;

                        (mapRef.current!.getSource('orders') as mapboxgl.GeoJSONSource).getClusterLeaves(clusterInfo.clusterId, 1, 0, (err, leaves) => {
                            if (err || !leaves || leaves.length === 0) return;

                            try {
                                const orderData = JSON.parse(leaves[0].properties!.orderData);
                                const el = createCustomMarkerRef.current?.(orderData, clusterInfo.coordinates);
                                const newMarker = new mapboxgl.Marker({ element: el }).setLngLat(clusterInfo.coordinates);

                                el.addEventListener('click', () => {
                                    if (orderData.masterOrderId) {
                                        navigate(`${PATH.TRACK}/${orderData.masterOrderId}`);
                                    }
                                });

                                currentMarkers[clusterInfo.markerId] = newMarker;
                                newMarker.addTo(mapRef.current!);
                            } catch (parseError) {
                                logError(parseError, {
                                    errorMessage: 'Failed to parse cluster order data',
                                    errorLocation: 'Track/index: renderMarkers cluster',
                                    storeInDatabase: true,
                                    logToConsole: true,
                                });
                            }
                        });
                    } else {
                        newMarkers[id] = existingMarker;
                        if (!markersOnScreen[id]) existingMarker.addTo(mapRef.current!);
                    }
                }
            }

            // Remove markers that have scrolled off-screen
            for (const id in markersOnScreen) {
                if (!newMarkers[id]) markersOnScreen[id].remove();
            }

            markersOnScreen = newMarkers;
        };

        mapRef.current!.on('render', () => {
            if (!mapRef.current!.isSourceLoaded('orders')) return;
            updateMarkers();
        });

        // Throttled tooltip position recalculation on map pan/zoom (~60 fps cap)
        const updateAllTooltipPositions = () => {
            document.querySelectorAll('.parent-marker').forEach((element: any) => {
                if (typeof element._updatePosition === 'function') {
                    element._updatePosition();
                }
            });
        };

        const throttledUpdateTooltipPositions = (() => {
            let timeoutId: NodeJS.Timeout | null = null;

            return () => {
                if (timeoutId) return;
                timeoutId = setTimeout(() => {
                    updateAllTooltipPositions();
                    timeoutId = null;
                }, 16);
            };
        })();

        // Close all detailed popups (restore small tooltips) during map interactions
        const closeAllDetailedPopups = () => {
            document.querySelectorAll('.parent-marker').forEach((marker: any) => {
                const popup = marker._popup;

                if (popup) {
                    popup.remove();
                    marker._popup = null;
                }
            });
            document.querySelectorAll('.order-tooltip').forEach((tooltip: any) => {
                tooltip.style.display = 'block';
            });
        };

        mapRef.current!.on('move', throttledUpdateTooltipPositions);
        mapRef.current!.on('zoom', throttledUpdateTooltipPositions);
        mapRef.current!.on('resize', updateAllTooltipPositions);

        mapRef.current!.on('zoomstart', closeAllDetailedPopups);
        mapRef.current!.on('movestart', closeAllDetailedPopups);
        mapRef.current!.on('dragstart', closeAllDetailedPopups);
        mapRef.current!.on('rotatestart', closeAllDetailedPopups);

        // DEBUG — remove before merging.
        // Pan/zoom to the perfect view, wait for the map to stop, then
        // copy the SW and NE values into NORTH_AMERICA_BOUNDS above.
        mapRef.current!.on('idle', () => {
            if (!mapRef.current) return;
            const b = mapRef.current.getBounds();
            console.log(`[Radar bounds] SW: [${b.getWest().toFixed(4)}, ${b.getSouth().toFixed(4)}]  NE: [${b.getEast().toFixed(4)}, ${b.getNorth().toFixed(4)}]`);
        });

        // Close detailed popups when clicking on empty map areas
        mapRef.current!.on('click', (e) => {
            const features = mapRef.current!.queryRenderedFeatures(e.point);
            const target = e.originalEvent.target as HTMLElement | null;
            const clickedOnMarker = features.some((f) => f.source === 'orders') || (target && target.closest && target.closest('.parent-marker'));

            if (!clickedOnMarker) closeAllDetailedPopups();
        });
    }, [mapRef, navigate]);

    // Initializes the cluster source, layers, and event listeners on the shared
    // map. Idempotent — the source-already-exists check prevents double-setup.
    // Toggle the shared map between flat (mercator) and 3D (globe).
    // Projection is map-INSTANCE state, not style state, so it needs no source/layer
    // re-add and survives a theme change untouched.
    const toggleProjection = useCallback(() => {
        if (!mapRef.current) return;

        const next = mapProjection === 'mercator' ? 'globe' : 'mercator';
        mapRef.current.setProjection(next);
        setMapProjection(next);
        writeTrackMapPreferences(userId, { projection: next });
    }, [mapProjection, mapRef, userId]);

    const initializeMap = useCallback(() => {
        // Attach the single persistent map (owned by TrackMapLayout) into our slot.
        if (mapContainerRef.current) {
            attachMapTo(mapContainerRef.current);
        }

        if (mapRef.current) {
            // Restore the user's projection on every list mount / re-attach. The detail
            // page forces 'mercator' on the shared instance, so coming back from it would
            // otherwise silently drop a globe selection.
            mapRef.current.setProjection(mapProjectionRef.current);

            // Initial camera: back-from-detail → smooth ease to default overview;
            // fresh visit → jump to birth state then ease to overview.
            // Guarded by hasInitialCameraRunRef so a second run (when isMapReady
            // flips) doesn't clobber the animation already in progress.
            if (!hasInitialCameraRunRef.current) {
                hasInitialCameraRunRef.current = true;
                const isBackFromDetail = consumeCameFromDetail();

                requestAnimationFrame(() => {
                    if (!mapRef.current) return;

                    mapRef.current.resize();

                    if (isBackFromDetail) {
                        // Smooth pull-back to the North America overview
                        fitNorthAmerica({
                            duration: 1400,
                            easing: (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2),
                        });
                    } else {
                        // Snap to a starting point then ease into the North America overview
                        mapRef.current.jumpTo({ center: [-93.2709, 42.3935], zoom: 3, pitch: 0, bearing: 0 });
                        fitNorthAmerica({ duration: 600 });
                    }
                });
            }

            // Source/layers and listeners are set up separately: the source/layers are
            // re-runnable (a style change wipes them), the listeners are once-per-map.
            // The persistent map's load event fires only once, so isMapReady (context
            // state) tells us deterministically whether load has already happened.
            if (isMapReady) {
                addOrdersSourceAndLayers();
                registerMapListeners();
            }
        }
    }, [navigate, attachMapTo, mapRef, isMapReady, fitNorthAmerica, addOrdersSourceAndLayers, registerMapListeners]);

    // A theme change (map.setStyle) destroys every style-owned source and layer, so
    // re-add ours once the new style has loaded. TrackMapLayout bumps styleEpoch at
    // that moment. Skipped on the initial render (epoch 0) — runSetup already did it.
    //
    // Only sources/layers are re-added here: the map event listeners survive setStyle
    // and registerMapListeners is guarded so it can never double-bind.
    useEffect(() => {
        if (!styleEpoch) return;
        if (!mapRef.current) return;
        if (getActiveTrackView() !== 'list') return;

        addOrdersSourceAndLayers();
        // Force the next setOrderMarker to actually rebuild the (now empty) source
        // instead of bailing on the sortToMapReload guard.
        sortToMapReload.current = true;
        setOrderMarker();
    }, [styleEpoch, mapRef, addOrdersSourceAndLayers, setOrderMarker]);

    // Primary mount/unmount effect — claim ownership, set up map, clean up on real unmount.
    // The isMountedRef tracker (declared after this) flips false before this cleanup runs,
    // letting us distinguish "deps changed" from "actually unmounting".
    useEffect(() => {
        setActiveTrackView('list');
        initializeMap();

        return () => {
            if (isMountedRef.current) {
                // Dep change (initializeMap regenerated, e.g. isMapReady flipped).
                // The map is still ours — leave state intact.
                return;
            }

            // Real unmount (list → detail or list → somewhere else).
            sortToMapReload.current = false;
            markerUpdateIdRef.current++;
            hasInitialCameraRunRef.current = false;

            // Release ownership BEFORE emptying the source so any in-flight
            // setOrderMarker retry wakes up, sees != 'list', and bails.
            if (getActiveTrackView() === 'list') {
                setActiveTrackView(null);
            }

            // Clear list-specific map state
            if (mapRef.current) {
                const src = mapRef.current.getSource('orders') as mapboxgl.GeoJSONSource | undefined;

                if (src) {
                    src.setData({ type: 'FeatureCollection', features: [] });
                }

                if ((mapRef.current as any)._clearAllMarkers) {
                    (mapRef.current as any)._clearAllMarkers();
                }
            }

            // Hand the persistent map's DOM node back to the layout stash
            returnToStash();
        };
    }, [initializeMap, returnToStash, mapRef]);

    // Tracker: flips isMountedRef false on real unmount. Declared AFTER the main
    // effect so React runs THIS cleanup FIRST (reverse declaration order), letting
    // the destructive cleanup above detect "really unmounting".
    useEffect(() => {
        isMountedRef.current = true;
        sortToMapReload.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Re-plot markers whenever the track list changes
    useEffect(() => {
        if (trackList.length > 0) {
            setOrderMarker();
        } else {
            removeAllMarkers();

            if (mapRef.current?.isStyleLoaded()) {
                fitNorthAmerica({ duration: 500 });
            }
        }
    }, [trackList, fitNorthAmerica]);

    // Ensure markers are placed after map style finishes loading
    useEffect(() => {
        if (mapRef.current && trackList.length > 0) {
            const capturedId = markerUpdateIdRef.current;
            let timerId: ReturnType<typeof setTimeout> | null = null;

            const handleMapReady = () => {
                timerId = setTimeout(() => {
                    if (markerUpdateIdRef.current === capturedId) {
                        setOrderMarker();
                    }
                }, 100);
            };

            if (mapRef.current.isStyleLoaded()) {
                handleMapReady();
            } else {
                mapRef.current.on('styledata', handleMapReady);

                return () => {
                    mapRef.current?.off('styledata', handleMapReady);
                    if (timerId) clearTimeout(timerId);
                };
            }

            return () => {
                if (timerId) clearTimeout(timerId);
            };
        }
    }, [mapRef.current, trackList]);

    // Keep map sized correctly while the resize interval is active
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (mapRef.current && mapContainerRef.current && viewActiveTab === 'map_view') {
                mapRef.current.resize();
            }
        }, 50);

        return () => clearInterval(intervalId);
    }, [viewActiveTab]);

    const headerContent = (
        <Header
            Icon={() => <img src={globe01} alt="track" className="w-4 h-4" />}
            title={
                <div className="flex items-center md:gap-2 gap-1 flex-wrap">
                    {t('Radar.Radar')}
                    <SelectBox
                        name="viewMode"
                        size="xs"
                        options={filterArr}
                        value={filterArr.find((opt) => opt.value === viewActiveTab) ?? null}
                        onChangeFunc={handleViewChange}
                        isSearchable={false}
                        isClearable={false}
                        parentClassName="w-[94px]"
                        classComp=""
                    />
                    {/* Current date + time in the READER's timezone; ticks itself every
                        second (useTrackClock) so it never needs a page refresh. */}
                    <TrackHeaderClock className="md:ml-1" />
                </div>
            }
            rightSideContent={
                <div className="flex items-center sm:gap-x-2.5 gap-x-2">
                    {viewActiveTab === 'map_view' && (
                        <>
                            {/* Projection toggle — flat (mercator) ⇄ 3D (globe). The ICON shows the
                                current projection, the TOOLTIP names the action — same convention as
                                the Eye/EyeOff panel button beside it. */}
                            <TooltipCmp message={mapProjection === 'globe' ? t('Radar.Switch_To_Flat') : t('Radar.Switch_To_Globe')} childrenClassName="!flex">
                                <ButtonCmp
                                    size="square-sm"
                                    className="btn-outline-primary"
                                    icon={mapProjection === 'globe' ? <Globe01 className="w-4 h-4" /> : <Map01 className="w-4 h-4" />}
                                    onClick={toggleProjection}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>

                            <TrackMapThemePicker buttonClassName="btn-outline-primary" />

                            <TrackTimezoneToggle buttonClassName="btn-outline-primary" />

                            <TooltipCmp message={isVisible ? 'Hide Panel' : 'Show Panel'} childrenClassName="!flex">
                                <ButtonCmp
                                    size="square-sm"
                                    className="btn-outline-primary"
                                    icon={isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    onClick={() => setIsVisible((prev) => !prev)}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>
                        </>
                    )}
                </div>
            }
            mainContainerClassName={viewActiveTab === 'map_view' ? 'z-[2] !bg-transparent' : '!border-0'}
        />
    );

    return (
        <PageSectionLayout
            header={viewActiveTab === 'map_view' ? <></> : headerContent}
            headerClassName={viewActiveTab === 'map_view' ? '!p-0' : ''}
            contentClassName={viewActiveTab === 'map_view' ? '!p-0 relative flex-1' : 'border-t border-borderPrimary'}
        >
            {/* ── MAP VIEW ── */}
            <div className={`w-full h-full ${viewActiveTab === 'map_view' ? 'relative' : 'hidden'}`}>
                {/* Full-screen map background */}
                <div className="absolute inset-0 radar-mapbox z-[1]">
                    <style>{`
                        /* Push the shared map's native zoom control (added once by
                           TrackMapLayout) below the floating header — same push the
                           detail page applies, since it's the same persistent control. */
                        .radar-mapbox .mapboxgl-ctrl-top-right {
                            padding-top: 68px;
                            padding-right: 8px;
                        }
                    `}</style>
                    <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
                </div>

                {/* Recenter — same position/icon/component as the detail page's Recenter
                    button (right-[18px] top-[145px]) so it doesn't jump when navigating
                    between /track and /track/:id. Zoom itself is the persistent native
                    control added once by TrackMapLayout — no custom zoom buttons needed
                    here any more. */}
                {mapRef.current && (
                    <div className="absolute z-[12] right-[18px] top-[145px]">
                        <TooltipCmp message={t('Radar.Recenter_Map', 'Recenter map')} childrenClassName="!flex">
                            <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={() => fitNorthAmerica({ duration: 500 })} icon={<Move className="w-4 h-4 text-textSecondary" />}>
                                <></>
                            </ButtonCmp>
                        </TooltipCmp>
                    </div>
                )}

                {/* Overlay: header + floating left sidebar */}
                <div className="absolute xxl:px-4 px-3 pt-4 w-full space-y-2 h-full flex flex-col overflow-hidden z-[2] pointer-events-none">
                    <div className={`pointer-events-auto radar-header-blur ${trackHeaderThemeClass}`}>{headerContent}</div>
                    <div
                        className={`w-[537px] flex-1 overflow-auto z-[2] flex flex-col pb-4 px-2 -mx-2 pt-2 space-y-2 transition-transform duration-300 ease-[cubic-bezier(.25,.1,.25,1)] pointer-events-auto scrollbar-hide ${
                            isVisible ? 'translate-x-0' : '-translate-x-full'
                        }`}
                    >
                        <TrackLeftSidebar onOrderHover={showOrderDetailPopup} onOrderHoverEnd={hideOrderDetailPopup} />
                    </div>
                </div>
            </div>

            {/* ── TABLE VIEW ── */}
            <div className={`w-full ${viewActiveTab === 'map_view' ? 'hidden' : 'block'}`}>
                <TrackTableView />
            </div>
        </PageSectionLayout>
    );
};

export default Track;
